// TODO REFINE THIS FILE!

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { OrgPluginsAPI } from '@makyoapp/makyo-sdk'
import { PluginAdapter } from '@makyoapp/makyo-common'
import { Check16, Close8, Edit16, Trash16, User16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, DeleteModal, MoreDropdown, SearchBox, SimpleInput } from '@makyoapp/makyo-ui'

import { toasted } from '@/lib/toast'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { useReqError } from '@/hooks/useReqError'

interface Service {
  name: string
  type: string
  status: string
  statusType: 'success' | 'warning' | 'error'
  lastUpdated: string
  created: string
  icon: string
  pluginData: PluginAdapter
  credentialId: string
  savedBackendValues: Record<string, any>
}

interface ServiceCardProps {
  service: Service
  onDelete: (credentialId: string, credentialName: string) => void
  onRename: (credentialId: string, newName: string) => Promise<void>
}

const getServiceIcon = (iconType: string) => {
  const iconClasses = 'w-6 h-6'

  switch (iconType) {
    case 'gmail':
      return (
        <div className='w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden'>
          <img
            src='https://upload.wikimedia.org/wikipedia/commons/7/7e/Gmail_icon_%282020%29.svg'
            alt='Gmail'
            className='w-full h-full object-contain'
            onError={(e) => {
              // Fallback to colored div if image fails to load
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.parentElement!.innerHTML = `
                <div class="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <span class="text-white font-bold text-sm">M</span>
                </div>
              `
            }}
          />
        </div>
      )
    case 'sheets':
      return (
        <div className='w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden'>
          <img
            src='https://upload.wikimedia.org/wikipedia/commons/thumb/3/30/Google_Sheets_logo_%282014-2020%29.svg/98px-Google_Sheets_logo_%282014-2020%29.svg.png?20201024100414'
            alt='Google Sheets'
            className='w-full h-full object-contain'
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.parentElement!.innerHTML = `
                <div class="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                  <span class="text-white font-bold text-sm">S</span>
                </div>
              `
            }}
          />
        </div>
      )
    case 'auth':
      return (
        <div className='w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden'>
          <img
            src='https://upload.wikimedia.org/wikipedia/commons/9/93/Google_Keep_icon_logo.svg'
            alt='Authentication'
            className='w-full h-full object-contain'
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.parentElement!.innerHTML = `
                <div class="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span class="text-white font-bold text-sm">A</span>
                </div>
              `
            }}
          />
        </div>
      )
    case 'anthropic':
      return (
        <div className='w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden'>
          <img
            src='https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Anthropic_logo.svg/1600px-Anthropic_logo.svg.png'
            alt='Anthropic'
            className='w-full h-full object-contain'
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.parentElement!.innerHTML = `
                <div class="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center">
                  <span class="text-white font-bold text-sm">A</span>
                </div>
              `
            }}
          />
        </div>
      )
    case 'supabase':
      return (
        <div className='w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden'>
          <img
            src='https://images.seeklogo.com/logo-png/43/1/supabase-logo-png_seeklogo-435677.png'
            alt='Supabase'
            className='w-full h-full object-contain'
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.parentElement!.innerHTML = `
                <div class="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
                  <span class="text-white font-bold text-sm">S</span>
                </div>
              `
            }}
          />
        </div>
      )
    case 'google':
      return (
        <div className='w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden'>
          <img
            src='https://static.dezeen.com/uploads/2025/05/sq-google-g-logo-update_dezeen_2364_col_0.jpg'
            alt='Google'
            className='w-full h-full object-contain'
            onError={(e) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              target.parentElement!.innerHTML = `
                <div class="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <span class="text-white font-bold text-sm">G</span>
                </div>
              `
            }}
          />
        </div>
      )
    default:
      return (
        <div className='w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center'>
          <User16 className={iconClasses} />
        </div>
      )
  }
}

function Credential({ service, onDelete, onRename }: ServiceCardProps) {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [isRenaming, setIsRenaming] = useState(false)
  const [tempName, setTempName] = useState(service.name)
  const navigate = useNavigate()

  const handleEdit = () => {
    handleRename()
  }

  const handleDelete = async () => {
    setIsDeleting(true)
    try {
      await onDelete(service.credentialId, service.name)
      setIsDeleteDialogOpen(false)
    } catch (error) {
      // Error handling is done in the parent component
    } finally {
      setIsDeleting(false)
    }
  }

  const handleRename = () => {
    setIsEditing(true)
    setTempName(service.name)
  }

  const handleCancelRename = () => {
    setIsEditing(false)
    setTempName(service.name)
  }

  const handleSaveRename = async () => {
    const newName = tempName.trim()
    if (newName === service.name || !newName) {
      handleCancelRename()
      return
    }

    setIsRenaming(true)
    try {
      await onRename(service.credentialId, newName)
      setIsEditing(false)
    } catch (error) {
      // Error handling is done in the parent component
      setTempName(service.name) // Reset to original name
    } finally {
      setIsRenaming(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveRename()
    } else if (e.key === 'Escape') {
      handleCancelRename()
    }
  }

  return (
    <>
      <div className='bg-white dark:bg-slate-800 text-card-foreground border border-border rounded-lg p-4 hover:bg-accent/50 transition-colors group dark:border-slate-500'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            {getServiceIcon(service.icon)}

            <div className='flex-1'>
              <div className='flex items-center space-x-3'>
                {isEditing ? (
                  <div className='flex items-center space-x-2'>
                    <SimpleInput
                      value={tempName}
                      onChange={(e) => setTempName(e.target.value)}
                      onKeyDown={handleKeyDown}
                      className='text-foreground font-medium'
                      autoFocus
                      disabled={isRenaming}
                    />
                    <Button
                      variant={ButtonVariant.Normal}
                      icon={<Check16 />}
                      onClick={handleSaveRename}
                      disabled={isRenaming}
                      className='shrink-0'
                      noPadding
                      squared
                    />
                    <Button
                      variant={ButtonVariant.Ghost}
                      icon={<Close8 />}
                      onClick={handleCancelRename}
                      disabled={isRenaming}
                      className='shrink-0'
                      noPadding
                      squared
                    />
                  </div>
                ) : (
                  <div className='flex items-center space-x-2'>
                    <h3 className='text-foreground font-medium'>{service.name}</h3>
                    {/* <Button
                      variant={ButtonVariant.Ghost}
                      icon={<Edit16 />}
                      onClick={handleRename}
                      className='opacity-0 group-hover:opacity-100 transition-opacity'
                      noPadding
                      squared
                    /> */}
                  </div>
                )}
                {service.statusType === 'warning' && (
                  <span className='bg-yellow-600 text-yellow-100 text-xs px-2 py-1 rounded-full'>{service.status}</span>
                )}
              </div>
              <div className='flex items-center space-x-2 mt-1'>
                <span className='text-muted-foreground text-sm'>{service.type}</span>
                <span className='text-muted-foreground text-sm'>|</span>
                <span className='text-muted-foreground text-sm'>Last updated {service.lastUpdated}</span>
                <span className='text-muted-foreground text-sm'>|</span>
                <span className='text-muted-foreground text-sm'>Created {service.created}</span>
              </div>
            </div>
          </div>

          <div className='flex items-center'>
            <MoreDropdown id={`credential-more-${service.name}`} placement='bottom-end'>
              <MoreDropdown.MenuItem
                className='credential-edit-act'
                id={`${service.name}-credential-edit-act`}
                onClick={handleEdit}
              >
                <Edit16 />
                Rename
              </MoreDropdown.MenuItem>
              <MoreDropdown.MenuItem
                className='credential-delete-act red'
                id={`${service.name}-credential-delete-act`}
                onClick={() => setIsDeleteDialogOpen(true)}
              >
                <Trash16 />
                Delete
              </MoreDropdown.MenuItem>
            </MoreDropdown>
          </div>
        </div>
      </div>

      <DeleteModal
        show={isDeleteDialogOpen}
        onHide={() => !isDeleting && setIsDeleteDialogOpen(false)}
        onSubmit={handleDelete}
        onCancel={() => !isDeleting && setIsDeleteDialogOpen(false)}
        title='Delete Credential?'
        withCancel={true}
        cancelLabel='Cancel'
        continueLabel='Yes, delete'
        isLoading={isDeleting}
      >
        <p>Are you sure you want to delete &quot;{service.name}&quot;? This may break any workflows that use it.</p>
      </DeleteModal>
    </>
  )
}

/**
 * Org Plugins Credentials Page
 *
 * @route `/o/:orgId/plugins/credentials`
 */
export default function PluginsCredentialsPage() {
  const { currentOrg } = useCurrentOrgContext()
  const printAPIError = useReqError()
  const [searchTerm, setSearchTerm] = useState('')
  const [services, setServices] = useState<Service[]>([])
  const [loading, setLoading] = useState(true)

  const handleDeleteCredential = async (credentialId: string, credentialName: string) => {
    if (!currentOrg) throw new Error('No organization selected')

    try {
      // Using the method name as requested by the user
      await OrgPluginsAPI.ReqDelete(currentOrg.id, credentialId)

      // Remove the deleted credential from the services list
      setServices((prevServices) => prevServices.filter((service) => service.credentialId !== credentialId))

      toasted(`Credential "${credentialName}" deleted successfully`)
    } catch (error) {
      printAPIError(error as Error)
      throw error // Re-throw to let the Credential component handle loading state
    }
  }

  const handleRenameCredential = async (credentialId: string, newName: string) => {
    if (!currentOrg) throw new Error('No organization selected')

    try {
      await OrgPluginsAPI.ReqRename(currentOrg.id, credentialId, newName)

      // Update the credential name in the services list
      setServices((prevServices) =>
        prevServices.map((service) => (service.credentialId === credentialId ? { ...service, name: newName } : service))
      )

      toasted(`Credential renamed to "${newName}" successfully`)
    } catch (error) {
      printAPIError(error as Error)
      throw error // Re-throw to let the Credential component handle loading state
    }
  }

  useEffect(() => {
    const fetchCredentials = async () => {
      if (!currentOrg) return

      try {
        setLoading(true)
        const response = await OrgPluginsAPI.ReqList(currentOrg.id)

        // Map API response to Service format
        const mappedServices: Service[] =
          response._Data?.map((credential: any) => {
            // Create plugin adapter from the plugin definition + saved credential values
            const pluginAdapter: PluginAdapter = {
              id: credential._Plugin._id || '',
              name: credential._Plugin._Name || 'Unknown Plugin',
              description: credential._Plugin._Notes?.en || '',
              icon: credential._Plugin._Icon || '',
              author: {
                id: '',
                name: credential._Plugin._Developer || '',
                email: '',
              },
              isInstalled: true,
              details: '',
              categories: credential._Plugin._Category ? [credential._Plugin._Category] : [],
              types: [],
              version: credential._Plugin._Version || '1.0.0',
              rating: undefined,
              price: 'free',
              reviews: [],
              installCount: 0,
              screenshots: [],
              updated: new Date(credential._CreatedAt || Date.now()),
              instructions: '',
              isActive: true,
              // Backend requirements definitions from the plugin
              backend: credential._Plugin._Backend || [],
              frontend: credential._Plugin._Frontend || {},
              auth: {
                type: credential._Plugin._Auth._Type || '',
                label: credential._Plugin._Auth._Label || '',
                description: credential._Plugin._Auth._Description || '',
              },
            }

            return {
              name: credential._Label || credential._Plugin._Name || 'Unknown Plugin',
              type: credential._Plugin._Name || 'Unknown Type',
              status: 'Active',
              statusType: 'success' as const,
              lastUpdated: 'Recently',
              created: new Date(credential._CreatedAt || Date.now()).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }),
              icon: credential._Plugin._Name?.toLowerCase() || 'default',
              pluginData: pluginAdapter,
              credentialId: credential._id,
              savedBackendValues: credential._Backend || {},
            }
          }) || []
        setServices(mappedServices)
      } catch (error) {
        console.error('Failed to fetch plugin credentials:', error)
        setServices([])
      } finally {
        setLoading(false)
      }
    }

    fetchCredentials()
  }, [currentOrg])

  const filteredServices = services.filter((service) => {
    const matchesSearch =
      service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      service.type.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  if (loading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <div className='text-muted-foreground'>Loading credentials...</div>
      </div>
    )
  }

  return (
    <div className='space-y-6'>
      {/* Search Section */}
      <div className='relative'>
        <SearchBox
          value={searchTerm}
          onSearch={setSearchTerm}
          onClearSearch={() => setSearchTerm('')}
          noBorder
          className='border-b w-full bdr bg-white dark:bg-slate-800'
        />
      </div>

      {/* Services List */}
      <div className='space-y-3'>
        {filteredServices.map((service, index) => (
          <Credential
            key={index}
            service={service}
            onDelete={handleDeleteCredential}
            onRename={handleRenameCredential}
          />
        ))}
      </div>

      {filteredServices.length === 0 && !loading && (
        <div className='text-center py-8'>
          <p className='text-muted-foreground'>
            {services.length === 0
              ? 'No plugin credentials found. Add some plugins first.'
              : 'No credentials found matching your search.'}
          </p>
        </div>
      )}
    </div>
  )
}
